import { Controller } from '@hotwired/stimulus';
import TemplateToDomController from 'controllers/template_to_dom_controller';

export default class ScriptsDependencyConfigurationController extends Controller {
  public static targets = [
    'input',
    'templateDependency',
    'templateConstant',
    'dependencies',
  ];

  public static values = {
    template: String,
    constantTag: String,
  };

  private declare inputTarget: HTMLTextAreaElement;
  private declare dependenciesTarget: HTMLUListElement;
  private declare templateDependencyTarget: HTMLTemplateElement;
  private declare templateConstantTarget: HTMLTemplateElement;
  private declare templateValue: string;
  private declare constantTagValue: string;
  private restoreInput: HTMLTextAreaElement | undefined;

  public connect(): void {
    this.inflate();

    this.restoreInput = this.inputTarget;
    this.inputTarget.remove();
  }

  public disconnect(): void {
    this.dependenciesTarget.innerText = '';

    if (this.restoreInput) {
      this.element.prepend(this.restoreInput);
    }
  }

  private inflate() {
    const initialValue = JSON.parse(this.inputTarget.value) as Record<
      string,
      Array<Record<string, string | boolean>>
    >;
    for (const key in initialValue) {
      if (!Object.prototype.hasOwnProperty.call(initialValue, key)) {
        continue;
      }

      const tag = key;
      for (const entry of initialValue[key]) {
        const shouldTriggerOnUpdate = Boolean(
          entry['should_trigger_on_update'],
        );
        const variableName = entry['variable_name'];
        const variableType = entry['variable_type'];
        const isConstant = tag === this.constantTagValue;

        const idMapping = { ':dependency_id': '' };
        const inflated = TemplateToDomController.inflate(
          isConstant
            ? this.templateConstantTarget
            : this.templateDependencyTarget,
          idMapping,
        );
        this.dependenciesTarget.append(inflated);

        const querySelectorBase = `[name="${this.templateValue.replace(
          ':dependency_id',
          idMapping[':dependency_id'],
        )}"]`;

        if (isConstant) {
          const dataType = entry['data_type'];
          const inputDataType =
            this.dependenciesTarget.querySelector<HTMLInputElement>(
              querySelectorBase.replace('::', 'data_type'),
            );
          if (inputDataType) {
            inputDataType.value = String(dataType);
          }

          const constantValue = entry['value'];
          const inputConstantValue =
            this.dependenciesTarget.querySelector<HTMLInputElement>(
              querySelectorBase.replace('::', 'value'),
            );
          if (inputConstantValue) {
            inputConstantValue.value = String(constantValue);
          }
        }

        const inputTag =
          this.dependenciesTarget.querySelector<HTMLInputElement>(
            querySelectorBase.replace('::', 'tag'),
          );
        if (inputTag) {
          inputTag.value = String(tag);
        }

        const inputVariableName =
          this.dependenciesTarget.querySelector<HTMLInputElement>(
            querySelectorBase.replace('::', 'variable_name'),
          );
        if (inputVariableName) {
          inputVariableName.value = String(variableName);
        }

        const optionVariableType = this.dependenciesTarget
          .querySelector<HTMLSelectElement>(
            querySelectorBase.replace('::', 'variable_type'),
          )
          ?.querySelector<HTMLOptionElement>(`option[value="${variableType}"]`);
        if (optionVariableType) {
          optionVariableType.selected = true;
        }

        const shouldTriggerOnUpdateCheckbox =
          this.dependenciesTarget.querySelector<HTMLInputElement>(
            querySelectorBase.replace('::', 'should_trigger_on_update'),
          );
        if (shouldTriggerOnUpdateCheckbox) {
          shouldTriggerOnUpdateCheckbox.checked = shouldTriggerOnUpdate;
        }
      }
    }
  }
}
